import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { getDifficultyClass, getEnjoymentClass } from "../pages/celeste-stats/StatsUtil";
import { Container, ProgressBar, Alert, Nav, Form, Badge } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router";
import { getContrastTextColor } from "../util/OtherUtils";
import {
  ClearsDifficultyListIcon,
  ClearsEnjoymentListIcon,
  GoldensDifficultyListIcon,
  GoldensEnjoymentListIcon,
  JournalIcon,
  ModListIcon,
  TopGoldenListIcon,
} from "./Icons";

export function LoadingContainer({ title, fluid, centered }) {
  let centerClass = centered ? "text-center" : "";
  return (
    <Container fluid={fluid}>
      {title ? <h1 className={centerClass}>{title}</h1> : null}
      <h3 className={centerClass}>
        Loading <Spinner />
      </h3>
    </Container>
  );
}

export function ErrorContainer({ title, message, fluid }) {
  return (
    <Container fluid={fluid}>
      <h1>{title}</h1>
      <Alert variant="danger">{message}</Alert>
    </Container>
  );
}

export function Spinner() {
  return <FontAwesomeIcon spin icon={faSpinner} />;
}

export function DifficultyDisplay({ difficulty, rank }) {
  let difficultyColorClass = getDifficultyClass(difficulty);
  let rankDisplay = rank ? `#${rank}` : "";

  if (rank) {
    return (
      <div className="d-flex justify-content-center align-items-center">
        <ProgressBar
          style={{ border: "1px solid lightgray", flex: "1" }}
          variant={difficultyColorClass}
          now={difficulty}
          max={100}
          label={`${difficulty}`}
        />
        <div
          className="d-none d-xxl-block ps-3"
          style={{ minWidth: "60px", paddingBottom: "2px", textAlign: "left" }}
        >
          {rankDisplay}
        </div>
      </div>
    );
  }

  return (
    <ProgressBar
      style={{ border: "1px solid lightgray", flex: "1" }}
      variant={difficultyColorClass}
      now={difficulty}
      max={100}
      label={`${difficulty}`}
    />
  );
}

export function EnjoymentDisplay({ enjoyment, rank, progressStyle, ...props }) {
  let enjoymentColorClass = getEnjoymentClass(enjoyment);
  let rankDisplay = rank ? `#${rank}` : "";

  if (rank) {
    return (
      <div className="d-flex justify-content-center align-items-center" {...props}>
        <ProgressBar
          style={{ border: "1px solid lightgray", flex: "1", ...progressStyle }}
          variant={enjoymentColorClass}
          now={enjoyment}
          max={10}
          label={`${enjoyment}`}
        />
        <div
          className="d-none d-xxl-block ps-3"
          style={{ minWidth: "60px", paddingBottom: "2px", textAlign: "left" }}
        >
          {rankDisplay}
        </div>
      </div>
    );
  }

  return (
    <ProgressBar
      style={{ border: "1px solid lightgray", flex: "1", ...progressStyle }}
      variant={enjoymentColorClass}
      now={enjoyment}
      max={10}
      label={`${enjoyment}`}
      {...props}
    />
  );
}

export function PlayerProfileNavbar({ playerName, activePage }) {
  const navigate = useNavigate();
  const location = useLocation();

  function navigateOnClick(event) {
    event.preventDefault();
    console.log("Navigating to -> ", event.target.pathname);
    navigate(event.target.pathname);
  }

  return (
    <Nav variant="tabs" defaultActiveKey={activePage} className="mb-3">
      <Nav.Item>
        <Nav.Link href={"/player/" + playerName} onClick={navigateOnClick}>
          <ModListIcon className="me-2" />
          All Mods
        </Nav.Link>
      </Nav.Item>
      <Nav.Item>
        <Nav.Link href={"/player/" + playerName + "/campaigns"} onClick={navigateOnClick}>
          <JournalIcon className="me-2" />
          Campaigns
        </Nav.Link>
      </Nav.Item>
      <Nav.Item>
        <Nav.Link href={"/player/" + playerName + "/top-golden-list"} onClick={navigateOnClick}>
          <TopGoldenListIcon className="me-2" />
          Top Golden List
        </Nav.Link>
      </Nav.Item>
      <Nav.Item>
        <Nav.Link href={"/player/" + playerName + "/list/clears/enjoyment"} onClick={navigateOnClick}>
          <ClearsEnjoymentListIcon className="me-2" />
          Clears By Enjoyment
        </Nav.Link>
      </Nav.Item>
      <Nav.Item>
        <Nav.Link href={"/player/" + playerName + "/list/clears/difficulty"} onClick={navigateOnClick}>
          <ClearsDifficultyListIcon className="me-2" />
          Clears By Difficulty
        </Nav.Link>
      </Nav.Item>
      <Nav.Item>
        <Nav.Link href={"/player/" + playerName + "/list/goldens/enjoyment"} onClick={navigateOnClick}>
          <GoldensEnjoymentListIcon className="me-2" />
          Goldens By Enjoyment
        </Nav.Link>
      </Nav.Item>
      <Nav.Item>
        <Nav.Link href={"/player/" + playerName + "/list/goldens/difficulty"} onClick={navigateOnClick}>
          <GoldensDifficultyListIcon className="me-2" />
          Goldens By Difficulty
        </Nav.Link>
      </Nav.Item>
    </Nav>
  );
}

//#region Sheet Difficulty Components
//Goldberries.net tiers, hardest first. ids match goldberries' difficulty ids.
//old_name is the name before goldberries' tier rework, still accepted when bulk adding maps.
export const SHEET_DIFFICULTIES = [
  { id: 0, name: "None", sort: -2, color: null, old_name: null },
  { id: 26, name: "Tier 22", sort: 22, color: "#bd60ff", old_name: null },
  { id: 25, name: "Tier 21", sort: 21, color: "#d863ff", old_name: null },
  { id: 24, name: "Tier 20", sort: 20, color: "#f266ff", old_name: "High Tier 0" },
  { id: 2, name: "Tier 19", sort: 19, color: "#ff68d9", old_name: "Mid Tier 0" },
  { id: 3, name: "Tier 18", sort: 18, color: "#ff6daa", old_name: "Low Tier 0" },
  { id: 23, name: "Tier 17", sort: 17, color: "#ff6d79", old_name: null },
  { id: 4, name: "Tier 16", sort: 16, color: "#ff7c70", old_name: "High Tier 1" },
  { id: 5, name: "Tier 15", sort: 15, color: "#ff9572", old_name: "Mid Tier 1" },
  { id: 6, name: "Tier 14", sort: 14, color: "#ffae75", old_name: "Low Tier 1" },
  { id: 7, name: "Tier 13", sort: 13, color: "#ffc677", old_name: "High Tier 2" },
  { id: 8, name: "Tier 12", sort: 12, color: "#ffdd7a", old_name: "Mid Tier 2" },
  { id: 9, name: "Tier 11", sort: 11, color: "#fff47c", old_name: "Low Tier 2" },
  { id: 10, name: "Tier 10", sort: 10, color: "#f4ff7f", old_name: "High Tier 3" },
  { id: 11, name: "Tier 9", sort: 9, color: "#d5ff82", old_name: "Mid Tier 3" },
  { id: 12, name: "Tier 8", sort: 8, color: "#b7ff84", old_name: "Low Tier 3" },
  { id: 14, name: "Tier 7", sort: 7, color: "#9bff87", old_name: "Tier 4" },
  { id: 15, name: "Tier 6", sort: 6, color: "#89ffb0", old_name: "Tier 5" },
  { id: 16, name: "Tier 5", sort: 5, color: "#8cffe2", old_name: "Tier 6" },
  { id: 17, name: "Tier 4", sort: 4, color: "#8eecff", old_name: "Tier 7" },
  { id: 22, name: "Tier 3", sort: 3, color: "#91c8ff", old_name: null },
  { id: 18, name: "Tier 2", sort: 2, color: "#93aeff", old_name: "Standard" },
  { id: 21, name: "Tier 1", sort: 1, color: "#9696ff", old_name: null },
  { id: 20, name: "No List", sort: 0, color: "#ffffff", old_name: null },
  { id: 19, name: "Undetermined", sort: -1, color: "#aaaaaa", old_name: null },
];

export function getSheetDifficulty(id) {
  return SHEET_DIFFICULTIES.find((difficulty) => difficulty.id === id);
}

export function SheetDifficultyIdSelect({ name, value, onChange }) {
  return (
    <Form.Select aria-label="Sheet Difficulty" name={name} value={value} onChange={onChange}>
      {SHEET_DIFFICULTIES.map((difficulty) => (
        <option key={difficulty.id} value={difficulty.id}>
          {difficulty.name}
        </option>
      ))}
    </Form.Select>
  );
}

export function SheetDifficultyBadge({ id, text, ...props }) {
  let difficulty = getSheetDifficulty(id);

  if (difficulty === undefined) {
    return null;
  }

  let backgroundColor = difficulty.color ?? "#808080";
  let badgeStyle = {
    backgroundColor: backgroundColor,
    color: getContrastTextColor(backgroundColor),
    border: "1px solid #888",
  };

  let additionalText = text !== undefined ? text : "";

  return (
    <Badge bg="" style={badgeStyle} {...props}>
      {additionalText}
      {difficulty.name}
    </Badge>
  );
}

//#endregion

export function VerticalRule({ color = "lightgray", style, ...props }) {
  return (
    <div
      style={{
        width: "1px",
        backgroundColor: color ? color : "lightgray",
        ...style,
      }}
      {...props}
    ></div>
  );
}
